<template>
    <v-card v-if="commissionForEdit">
        <v-card-text>
            <v-form
                ref="form"
                v-model="isValidGeneral"
                :disabled="isSaveLoading"
                data-cy="product-prices-commision-form"
            >
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model="commissionForEdit.planName"
                            :rules="[requiredRule('name')]"
                            density="compact"
                            hide-details
                            :label="`${$t('name')}*`"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <div class="full-width d-flex mt-1">
                            <v-spacer />
                            <v-switch
                                v-model="commissionForEdit.active"
                                color="primary"
                                hide-details
                                :label="$t('active')"
                            />
                        </div>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined" :loading="isTariffsLoading">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('commissions_active_during') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-data-table
                                    :items="commissionForEdit.tariffLinks"
                                    :headers="headersTariffs"
                                    :hide-default-header="isMobile"
                                    hide-default-footer
                                    :items-per-page="-1"
                                >
                                    <template v-slot:item.tariff="{item}">
                                        <div class="my-3">
                                            <v-select
                                                v-model="item.tariffId"
                                                :items="tariffItems(item.tariffId)"
                                                item-value="id"
                                                item-title="name"
                                                hide-details
                                                :density="'compact'"
                                                :disabled="item.tariffId && !tariffStatus(item.tariffId)"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.active="{item}">
                                        {{ tariffStatus(item.tariffId) ? $t('active') : $t('inactive') }}
                                    </template>
                                    <template v-slot:item.dates="{item}">
                                        <div class="d-flex align-center">
                                            <div class="me-4 full-width">
                                                <date-picker
                                                    v-model="item.dateFrom"
                                                    outlined
                                                    :density="'compact'"
                                                    :max="item.dateTo || '2100-01-01'"
                                                    :label="$t('date_from') + '*'"
                                                    :rules="[requiredRule('date_from')]"
                                                    hide-details
                                                />
                                            </div>
                                            <div class="full-width">
                                                <date-picker
                                                    v-model="item.dateTo"
                                                    outlined
                                                    :min="item.dateFrom || '1900-01-01'"
                                                    :density="'compact'"
                                                    :label="$t('date_to') + '*'"
                                                    :rules="[requiredRule('date_to')]"
                                                    hide-details
                                                />
                                            </div>
                                        </div>
                                    </template>
                                    <template v-slot:item.actions="{item, index}">
                                        <div class="full-width d-flex align-center">
                                            <v-spacer />
                                            <v-btn
                                                variant="text"
                                                size="small"
                                                color="primary"
                                                @click="copyTariffLink(item)"
                                            >
                                                {{ $t('copy') }}
                                            </v-btn>
                                            <v-btn
                                                size="small"
                                                color="error"
                                                variant="text"
                                                icon="mdi-close"
                                                @click="removeTariffLink(index)"
                                            />
                                        </div>
                                    </template>
                                    <template v-slot:item.mobile="{item, index}">
                                        <v-col cols="12" class="mt-4 px-0">
                                            <v-select
                                                v-model="item.tariffId"
                                                :items="tariffItems(item.tariffId)"
                                                item-value="id"
                                                item-title="name"
                                                hide-details
                                                :density="'compact'"
                                                :disabled="item.tariffId && !tariffStatus(item.tariffId)"
                                            />
                                            <div class="text-caption">
                                                {{ tariffStatus(item.tariffId) ? $t('active') : $t('inactive') }}
                                            </div>
                                        </v-col>
                                        <v-col cols="12" class="px-0">
                                            <date-picker
                                                v-model="item.dateFrom"
                                                outlined
                                                :density="'compact'"
                                                :max="item.dateTo || '2100-01-01'"
                                                :label="$t('date_from') + '*'"
                                                :rules="[requiredRule('date_from')]"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col cols="12" class="px-0">
                                            <date-picker
                                                v-model="item.dateTo"
                                                outlined
                                                :min="item.dateFrom || '1900-01-01'"
                                                :density="'compact'"
                                                :label="$t('date_to') + '*'"
                                                :rules="[requiredRule('date_to')]"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col cols="12" class="px-0">
                                            <div class="full-width d-flex align-center">
                                                <v-spacer />
                                                <v-btn
                                                    variant="text"
                                                    size="small"
                                                    color="primary"
                                                    @click="copyTariffLink(item)"
                                                >
                                                    {{ $t('copy') }}
                                                </v-btn>
                                                <v-btn
                                                    size="small"
                                                    color="error"
                                                    variant="text"
                                                    icon="mdi-close"
                                                    @click="removeTariffLink(index)"
                                                />
                                            </div>
                                        </v-col>
                                    </template>
                                </v-data-table>
                                <v-divider class="mb-4" />
                                <v-row dense>
                                    <v-col cols="12">
                                        <div class="full-width d-flex align-center">
                                            <v-btn variant="flat" size="small" color="primary" @click="addTariffLink">
                                                {{ $t('add') }}
                                            </v-btn>
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('description') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-textarea
                                    v-model="commissionForEdit.planDescription"
                                    :placeholder="`${$t('description')}`"
                                    rows="3"
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('currency_commission_calculation') }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-row>
                                    <v-col cols="12" md="3">
                                        <v-select
                                            v-model="commissionForEdit.currencyCode"
                                            :items="currencies"
                                            hide-details
                                            :label="`${$t('currency')}`"
                                        />
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ commissionPerLabel }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <CommissionAmountHotel
                                    v-if="contract.serviceType === useContractServiceType.ownHotel"
                                    v-model="commissionForEdit"
                                />
                                <NumberInputWithButtons
                                    v-else
                                    v-model="commissionForEdit.baseCommissionValue"
                                    :label="
                                        commissionForEdit.baseCommissionType === 'PERCENTAGE'
                                            ? $t('percent')
                                            : $t('amount')
                                    "
                                    :width="180"
                                    :buttons="[
                                        {
                                            icon: 'mdi-percent',
                                            active: commissionForEdit.baseCommissionType === 'PERCENTAGE',
                                            onClick: () => (commissionForEdit.baseCommissionType = 'PERCENTAGE'),
                                        },
                                        {
                                            icon: 'mdi-cash',
                                            active: commissionForEdit.baseCommissionType === 'ABSOLUTE',
                                            onClick: () => (commissionForEdit.baseCommissionType = 'ABSOLUTE'),
                                        },
                                    ]"
                                    :append-text="
                                        commissionForEdit.baseCommissionType === 'ABSOLUTE'
                                            ? commissionForEdit.currencyCode
                                            : null
                                    "
                                />
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row v-if="isShowServices" dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{
                                        $t('commission_applied_for', {
                                            prop: $t(`commissionServiceType.${contract.serviceType}`),
                                        })
                                    }}
                                </span>
                            </v-card-title>
                            <v-card-text>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <v-data-table
                                            v-model="commissionForEdit.servicesAvailability"
                                            :headers="[{title: $t('selected_all'), value: 'name'}]"
                                            :items="useProductServices"
                                            disable-sort
                                            density="compact"
                                            color="primary"
                                            data-cy="services-table"
                                            :items-per-page="-1"
                                            hide-default-footer
                                            show-select
                                        >
                                            <template v-slot:item.name="{item}">
                                                {{ getProductServiceName(item) }}
                                            </template>
                                        </v-data-table>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" md="12">
                        <v-card variant="outlined" :loading="isExtraServicesLoading">
                            <v-card-title>
                                <span class="font-weight-bold text-subtitle-2">
                                    {{ $t('commission_for_extra') }}
                                </span>
                            </v-card-title>
                            <v-card-text class="my-0">
                                <v-data-table
                                    :items="commissionForEdit.extraServiceCommissions"
                                    :headers="headersCommissionsExtraServices"
                                    hide-default-footer
                                    :items-per-page="-1"
                                >
                                    <template v-slot:body.prepend>
                                        <tr class="v-data-table__tr bg-grey-lighten-5">
                                            <td class="v-data-table__td v-data-table-column--align-start">
                                                <span class="font-weight-medium">{{ $t('base_commission') }}</span>
                                            </td>
                                            <td class="v-data-table__td v-data-table-column--align-start">
                                                <span />
                                            </td>
                                            <td class="v-data-table__td">
                                                <v-number-input
                                                    v-model="commissionForEdit.extraServiceCommissionValue"
                                                    hide-details
                                                    density="compact"
                                                    :label="
                                                        commissionForEdit.extraServiceCommissionType === 'PERCENTAGE'
                                                            ? `${$t('percent')}`
                                                            : `${$t('amount')}`
                                                    "
                                                    class="my-2"
                                                    :rules="[requiredRule('')]"
                                                    persistent-placeholder
                                                    :width="
                                                        commissionForEdit.extraServiceCommissionType === 'PERCENTAGE'
                                                            ? 208
                                                            : 250
                                                    "
                                                >
                                                    <template v-slot:append-inner>
                                                        <v-btn
                                                            size="x-small"
                                                            color="primary"
                                                            class="cursor-pointer"
                                                            variant="text"
                                                            :icon="
                                                                commissionForEdit.extraServiceCommissionType ===
                                                                'PERCENTAGE'
                                                                    ? 'mdi-percent'
                                                                    : 'mdi-cash'
                                                            "
                                                        />
                                                        <v-btn
                                                            size="x-small"
                                                            color="grey-lighten-1"
                                                            class="cursor-pointer"
                                                            variant="text"
                                                            :icon="
                                                                commissionForEdit.extraServiceCommissionType ===
                                                                'PERCENTAGE'
                                                                    ? 'mdi-cash'
                                                                    : 'mdi-percent'
                                                            "
                                                            @click="
                                                                commissionForEdit.extraServiceCommissionType =
                                                                    commissionForEdit.extraServiceCommissionType ===
                                                                    'PERCENTAGE'
                                                                        ? 'ABSOLUTE'
                                                                        : 'PERCENTAGE'
                                                            "
                                                        />
                                                    </template>
                                                    <template v-slot:append>
                                                        <div>
                                                            <span
                                                                v-if="
                                                                    commissionForEdit.extraServiceCommissionType ===
                                                                    'ABSOLUTE'
                                                                "
                                                                class="me-3"
                                                            >
                                                                {{ commissionForEdit.currencyCode }}
                                                            </span>
                                                            <v-btn
                                                                type="text"
                                                                size="x-small"
                                                                icon="mdi-select-arrow-down"
                                                                @click="applyForAllCommissionExtra"
                                                            />
                                                        </div>
                                                    </template>
                                                </v-number-input>
                                            </td>
                                        </tr>
                                    </template>
                                    <template v-slot:item.name="{item}">
                                        <span v-if="getOwnExtraService(item.extraServiceId)">
                                            {{ getOwnExtraService(item.extraServiceId).name[selectedLocale] }}
                                        </span>
                                    </template>
                                    <template v-slot:item.status="{item}">
                                        <span v-if="getOwnExtraService(item.extraServiceId)">
                                            {{
                                                getOwnExtraService(item.extraServiceId).active
                                                    ? $t('active')
                                                    : $t('inactive')
                                            }}
                                        </span>
                                    </template>
                                    <template v-slot:item.commission="{item}">
                                        <v-number-input
                                            v-model="item.value"
                                            hide-details
                                            density="compact"
                                            :label="item.type === 'PERCENTAGE' ? `${$t('percent')}` : `${$t('amount')}`"
                                            class="my-2"
                                            :rules="[requiredRule('')]"
                                            persistent-placeholder
                                            :width="item.type === 'PERCENTAGE' ? 176 : 207"
                                        >
                                            <template v-slot:append-inner>
                                                <v-btn
                                                    size="x-small"
                                                    color="primary"
                                                    class="cursor-pointer"
                                                    variant="text"
                                                    :icon="item.type === 'PERCENTAGE' ? 'mdi-percent' : 'mdi-cash'"
                                                />
                                                <v-btn
                                                    size="x-small"
                                                    color="grey-lighten-1"
                                                    class="cursor-pointer"
                                                    variant="text"
                                                    :icon="item.type === 'PERCENTAGE' ? 'mdi-cash' : 'mdi-percent'"
                                                    @click="
                                                        item.type =
                                                            item.type === 'PERCENTAGE' ? 'ABSOLUTE' : 'PERCENTAGE'
                                                    "
                                                />
                                            </template>
                                            <template v-slot:append>
                                                <span v-if="item.type === 'ABSOLUTE'">
                                                    {{ commissionForEdit.currencyCode }}
                                                </span>
                                            </template>
                                        </v-number-input>
                                    </template>
                                </v-data-table>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions class="d-flex justify-start px-6 pb-6 pt-4">
            <v-btn color="primary" :loading="isSaveLoading" data-cy="save" variant="elevated" @click="save">
                {{ $t('save') }}
            </v-btn>
            <v-btn data-cy="close" variant="text" @click="onClose">
                {{ $t('cancel') }}
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import DaterangeM from '~src/components/search/forms/daterangeM.src'
    import {clone} from '~/utils/helpers'
    import {contractServiceType} from '~/utils/types/types'

    @Component({
        components: {DaterangeM, DatePicker},
    })
    export class ProductPricesCommissionsModal extends mixins(FormValidationRulesMixin) {
        @Prop() commission
        @Prop() contract
        @Prop() selectedLocale

        isValidGeneral = false

        commissionForEdit = null

        isSaveLoading = false
        isTariffsLoading = false
        isExtraServicesLoading = false
        activeTariffs = []
        inactiveTariffs = []
        ownExtraServices = []

        editMode = false

        async mounted() {
            await this.initValues()
        }

        async initValues() {
            if (this.commission) {
                this.commissionForEdit = clone(this.commission)
            } else {
                this.commissionForEdit = {
                    contractId: this.contract.id,
                    baseCommissionType: 'PERCENTAGE',
                    baseCommissionValue: 0,
                    commissionType: 'TRIP',
                    extraServiceCommissionType: 'PERCENTAGE',
                    extraServiceCommissionValue: 0,
                    planName: '',
                    serviceCommissionType: 'PERCENTAGE',
                    serviceCommissionValue: 0,
                    active: true,
                    tariffLinks: [],
                    planDescription: '',
                    servicesAvailability: [],
                    forAllServices: true,
                    currencyCode: supplierStore.fullContract.transactionsCurrency || this.currencies[0].value,
                }

                if (this.contract.serviceType === contractServiceType.ownHotel) {
                    this.commissionForEdit.commissionType = 'ACCOMMODATION'

                    this.commissionForEdit.baseCommissionValue = 0
                    this.commissionForEdit.extrabedCommissionValue = 0
                    this.commissionForEdit.sharingCommissionValue = 0
                    this.commissionForEdit.mealCommissionValue = 0
                    this.commissionForEdit.serviceCommissionValue = 0

                    this.commissionForEdit.baseCommissionType = 'PERCENTAGE'
                    this.commissionForEdit.extrabedCommissionType = 'PERCENTAGE'
                    this.commissionForEdit.sharingCommissionType = 'PERCENTAGE'
                    this.commissionForEdit.mealCommissionType = 'PERCENTAGE'
                    this.commissionForEdit.serviceCommissionType = 'ABSOLUTE'
                }
            }

            if (this.isShowServices && this.commissionForEdit.forAllServices && useProductServices.value?.length) {
                this.commissionForEdit.servicesAvailability = useProductServices.value.map(({id}) => id)
            }

            this.editMode = !!this.commissionForEdit.id

            await Promise.all([this.loadTariffs(), this.loadOwnExtraServices()])

            if (this.editMode === false) {
                if (!this.commissionForEdit.tariffLinks.length) {
                    this.addTariffLink()
                }

                if (!this.commissionForEdit.extraServiceCommissions?.length) {
                    this.commissionForEdit.extraServiceCommissions = this.ownExtraServices.map(es => ({
                        extraServiceId: es.id,
                        type: 'PERCENTAGE',
                        value: 0,
                    }))
                }
            }
        }

        async loadTariffs() {
            try {
                this.isTariffsLoading = true
                const [activeTariffs, inactiveTariffs] = await Promise.all([
                    this.$api.tariffs.get({contractId: this.contract.id, active: true}),
                    this.$api.tariffs.get({contractId: this.contract.id, active: false}),
                ])
                this.activeTariffs = activeTariffs.tariffs
                this.inactiveTariffs = inactiveTariffs.tariffs
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isTariffsLoading = false
            }
        }

        async loadOwnExtraServices() {
            try {
                this.isExtraServicesLoading = true
                this.ownExtraServices = (
                    await this.$api.ownProductExtraServices.get({contractId: this.contract.id})
                ).extraServices
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isExtraServicesLoading = false
            }
        }

        clear() {
            this.commissionForEdit = null
            this.activeTariffs = []
            this.inactiveTariffs = []
            this.ownExtraServices = []
        }

        get tariffs() {
            return [...this.activeTariffs, ...this.inactiveTariffs]
        }

        tariffItems(id) {
            return !id || this.tariffStatus(id) ? this.activeTariffs : this.inactiveTariffs
        }

        tariffStatus(id) {
            return !!this.tariffs.find(tariff => tariff.id === id)?.active
        }

        @Emit()
        onClose() {
            this.clear()
        }

        @Emit()
        onSave() {}

        async save() {
            await this.$refs.form.validate()
            if (!this.isValidGeneral) return
            if (this.isShowServices) {
                this.commissionForEdit.forAllServices =
                    this.commissionForEdit.servicesAvailability.length === useProductServices.value.length
            }

            if (this.editMode) {
                await this.saveEdit()
            } else {
                await this.saveCreate()
            }
        }

        async saveEdit() {
            try {
                this.isSaveLoading = true

                await this.$api.ownProductCommissions.put(this.commissionForEdit)

                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.onClose()
                this.onSave()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.isSaveLoading = false
            }
        }

        async saveCreate() {
            try {
                this.isSaveLoading = true

                await this.$api.ownProductCommissions.post(this.commissionForEdit)

                this.$toast.success(this.$t('success_message.saved_successfully'))
                this.onClose()
                this.onSave()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.isSaveLoading = false
            }
        }

        removeTariffLink(tariffLinkIndex) {
            this.commissionForEdit.tariffLinks.splice(tariffLinkIndex, 1)
        }

        addTariffLink() {
            if (this.activeTariffs.length === 0) return

            this.commissionForEdit.tariffLinks.push({
                tariffId: this.activeTariffs[0].id,
                dateFrom: this.activeTariffs[0].dateFrom,
                dateTo: this.activeTariffs[0].dateTo,
                priority: this.commissionForEdit.tariffLinks.length + 1,
            })
        }

        copyTariffLink(tariffLink) {
            this.commissionForEdit.tariffLinks.push({
                tariffId: tariffLink.tariffId,
                dateFrom: tariffLink.dateFrom,
                dateTo: tariffLink.dateTo,
            })
        }

        getOwnExtraService(id) {
            return this.ownExtraServices.find(el => el.id === id)
        }

        applyForAllCommissionExtra() {
            this.commissionForEdit.extraServiceCommissions.forEach(el => {
                el.value = this.commissionForEdit.extraServiceCommissionValue
                el.type = this.commissionForEdit.extraServiceCommissionType
            })
        }

        getProductServiceName(item) {
            let name = ''
            const availabilityService = supplierStore.availabilityServices.find(el => el.serviceId === item.id)
            if (availabilityService?.categoryName) {
                name += `${availabilityService.categoryName}, ${item.name}`
            } else {
                name += `${item.name}`
            }

            if (item.minOccupancy && item.maxOccupancy) {
                name += `, ${item.minOccupancy} - ${item.maxOccupancy}`
            }

            return name
        }

        get useContractServiceType() {
            return contractServiceType
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get headersTariffs() {
            if (this.isMobile) {
                return [{title: '', value: 'mobile'}]
            }

            return [
                {title: this.$t('tariff'), value: 'tariff'},
                {title: this.$t('status'), value: 'active'},
                {title: this.$t('dates'), value: 'dates'},
                {title: this.$t('actions'), value: 'actions', align: 'end'},
            ]
        }

        get headersCommissionsExtraServices() {
            return [
                {title: this.$t('name'), value: 'name'},
                {title: this.$t('status'), value: 'status'},
                {title: this.$t('commission'), value: 'commission'},
            ]
        }

        get currencies() {
            return Object.values(this.$conf.public.currency.available)
                .filter(currency => Object.keys(runtimeStore.exchangeRates).includes(currency))
                .map(currency => ({
                    title: currency,
                    value: currency,
                }))
        }

        get isShowServices(): boolean {
            if (!this.contract?.serviceType) return false
            const availableServices: string[] = [
                contractServiceType.ownHotel,
                contractServiceType.ownExcursion,
                contractServiceType.concert,
                contractServiceType.ownTransfer,
                contractServiceType.ownCruise,
                contractServiceType.ownExtraService,
            ]
            return availableServices.includes(this.contract.serviceType)
        }

        get commissionPerLabel() {
            if (this.contract.serviceType === contractServiceType.ownHotel) {
                return this.$t('commission_per')
            }
            if (this.contract.serviceType === contractServiceType.ownTransfer) {
                return supplierStore.fullContract.transferType === 'PRIVATE'
                    ? this.$t('commission_per_service')
                    : this.$t('commission_per_tourist')
            }

            return this.$t('commission_per_tourist')
        }
    }

    export default ProductPricesCommissionsModal
</script>
